import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useNotification } from '../../contexts/NotificationContext';
import { supabase } from '../../services/supabase';
import { useAuth } from '../../contexts/AuthContext';

interface WishlistModalProps {
  isOpen: boolean;
  onClose: () => void;
  wishData?: WishFormData & { id?: number; status?: string };
  isSystem?: boolean;
  onStatusChange?: (id: number, newStatus: string) => Promise<void>;
}

export type WishType = 'new_functionality' | 'improve_functionality' | 'error' | 'other';

export interface WishFormData {
  name: string;
  type: WishType;
  feedback: string;
  page_url: string;
}

const STATUSES = ['submitted', 'analisys', 'in_development', 'done'];

const WishlistModal = ({
  isOpen,
  onClose,
  wishData,
  isSystem = false,
  onStatusChange,
}: WishlistModalProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { showNotification } = useNotification();
  const { user } = useAuth();
  const currentUserId = user?.id || null;
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>('');

  const [formData, setFormData] = useState<WishFormData>({
    name: '',
    type: 'new_functionality',
    feedback: '',
    page_url: location.pathname,
  });

  // Update form data when wishData changes
  useEffect(() => {
    if (wishData) {
      setFormData({
        name: wishData.name || '',
        type: wishData.type || 'new_functionality',
        feedback: wishData.feedback || '',
        page_url: wishData.page_url || location.pathname,
      });
      setSelectedStatus(wishData.status || 'Submitted');
    }
  }, [wishData, location.pathname]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleStatusChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newStatus = e.target.value;
    setSelectedStatus(newStatus);

    if (wishData?.id && onStatusChange) {
      setIsStatusUpdating(true);
      try {
        await onStatusChange(wishData.id, newStatus);
        showNotification(t('wishlist.status_update_success'), 'success');
      } catch (error) {
        console.error('Error updating status:', error);
        showNotification(t('wishlist.status_update_error'), 'error');
        setSelectedStatus(wishData.status || 'Submitted');
      } finally {
        setIsStatusUpdating(false);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const { error } = await supabase.from('user_feedback').insert([
        {
          ...formData,
          user_id: currentUserId,
          user_email: user?.email,
          user_name: `${user?.user_metadata.first_name || '-'} ${user?.user_metadata.last_name || '-'}`,
        },
      ]);

      if (error) {
        showNotification('Error adding wish', 'error');
        throw error;
      }

      showNotification(t('wishlist.submit_success'), 'success');

      setFormData({
        name: '',
        type: 'new_functionality',
        feedback: '',
        page_url: location.pathname,
      });
      onClose();
    } catch (error) {
      console.error('Error submitting wish:', error);
      showNotification(t('wishlist.submit_error'), 'error');
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden transform rounded-2xl bg-white text-left align-middle shadow-xl transition-all my-auto">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-gray-900 flex-shrink-0 p-6 pb-0"
                >
                  {wishData && isSystem ? t('wishlist.view_title') : t('wishlist.title')}
                </Dialog.Title>

                <div className="flex-1 min-h-0 overflow-y-auto p-6 pt-4">
                  {isSystem && wishData ? (
                    <div className="mt-4">
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">
                          {t('wishlist.name_label')}
                        </label>
                        <div className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 bg-gray-50">
                          {wishData.name}
                        </div>
                      </div>

                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">
                          {t('wishlist.type_label')}
                        </label>
                        <div className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 bg-gray-50">
                          {t(`wishlist.type_${wishData.type}`)}
                        </div>
                      </div>

                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">
                          {t('wishlist.description_label')}
                        </label>
                        <div className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 bg-gray-50 min-h-[100px] whitespace-pre-wrap">
                          {wishData.feedback}
                        </div>
                      </div>

                      <div className="mb-4">
                        <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                          {t('wishlist.status_label')}
                        </label>
                        <div className="relative">
                          <select
                            id="status"
                            name="status"
                            value={selectedStatus}
                            onChange={handleStatusChange}
                            disabled={isStatusUpdating}
                            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
                          >
                            {STATUSES.map((status) => (
                              <option
                                key={status}
                                value={status}
                                disabled={status === 'Submitted'} // Disable "Submitted" option
                              >
                                {t(`wishlist.status_${status}`)}
                              </option>
                            ))}
                          </select>
                          {isStatusUpdating && (
                            <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="mt-6 flex justify-end space-x-3">
                        <button
                          type="button"
                          onClick={onClose}
                          className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                        >
                          {t('common.close')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="mt-4">
                      <div className="mb-4">
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                          {t('wishlist.name_label')}
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
                        />
                      </div>

                      <div className="mb-4">
                        <label htmlFor="type" className="block text-sm font-medium text-gray-700">
                          {t('wishlist.type_label')}
                        </label>
                        <select
                          id="type"
                          name="type"
                          value={formData.type}
                          onChange={handleChange}
                          required
                          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
                        >
                          <option value="new_functionality">
                            {t('wishlist.type_new_functionality')}
                          </option>
                          <option value="improve_functionality">
                            {t('wishlist.type_improve_functionality')}
                          </option>
                          <option value="error">{t('wishlist.type_error')}</option>
                          <option value="other">{t('wishlist.type_other')}</option>
                        </select>
                      </div>

                      <div className="mb-4">
                        <label
                          htmlFor="feedback"
                          className="block text-sm font-medium text-gray-700"
                        >
                          {t('wishlist.description_label')}
                        </label>
                        <textarea
                          id="feedback"
                          name="feedback"
                          value={formData.feedback}
                          onChange={handleChange}
                          required
                          rows={4}
                          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
                        />
                      </div>

                      <div className="mt-6 flex justify-end space-x-3">
                        <button
                          type="button"
                          onClick={onClose}
                          className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                        >
                          {t('common.cancel')}
                        </button>
                        <button
                          type="submit"
                          className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                        >
                          {t('wishlist.submit')}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default WishlistModal;
